import { apiClient } from '@/lib/api-client'

// Types
export interface User {
  id: number
  email: string | null
  username: string | null
  first_name: string | null
  last_name: string | null
  full_name: string | null
  phone_number: string | null
  dob: string | null
  gender: string | null
  address: string | null
  is_active: boolean
  is_superuser: boolean
  is_hr_admin: boolean
  auth_method: 'email' | 'sms' | 'both' | null
  is_verified: boolean
  last_login: string | null
  created_at: string
  updated_at: string
  organization_id: number | null
  organization?: {
    id: number
    name: string
    slug: string
  } | null
  invited_at?: string | null
  safety_flagged_at: string | null
  analytics_snapshot?: UserAnalyticsSnapshot | null
  analytics_computed_at?: string | null
}

export interface UserAnalyticsSnapshot {
  total_messages: number
  total_resumes_uploaded: number
  total_job_matches_ran: number
  total_learning_skills: number
  last_signed_in: string | null
  status: string
  error_message: string | null
  computed_at: string | null
}

export interface Organization {
  id: number
  name: string
  slug: string
  description: string | null
  layoff_date: string | null
  website: string | null
  hr_contact_email: string | null
  hr_contact_name: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface OrganizationWithUserCount extends Organization {
  user_count: number
}

export interface OrganizationListParams {
  page?: number
  limit?: number
  search?: string
  is_active?: boolean
}

export interface OrganizationListResponse {
  organizations: OrganizationWithUserCount[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface OrganizationCreate {
  name: string
  slug?: string
  description?: string
  layoff_date?: string
  website?: string
  hr_contact_email?: string
  hr_contact_name?: string
  is_active?: boolean
}

export interface OrganizationUpdate {
  name?: string
  slug?: string
  description?: string
  layoff_date?: string
  website?: string
  hr_contact_email?: string
  hr_contact_name?: string
  is_active?: boolean
}

export interface UserListParams {
  page?: number
  limit?: number
  search?: string
  is_active?: boolean
  is_superuser?: boolean
  auth_method?: 'email' | 'sms' | 'both'
  organization_id?: number
}

export interface UserListResponse {
  users: User[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface AdminUserAnalyticsRefreshRequest {
  scope?: 'page' | 'user' | 'all'
  user_id?: number
  page?: number
  limit?: number
  search?: string
  is_active?: boolean
  is_superuser?: boolean
  auth_method?: 'email' | 'sms' | 'both'
  organization_id?: number
}

export interface AdminUserAnalyticsRefreshResponse {
  accepted: boolean
  job_id: string | null
  scope: string
  target_count: number
  message: string
}

export interface AdminUserAnalyticsRefreshStatus {
  job_id: string
  status: 'running' | 'completed' | 'failed'
  target_count: number
  processed_count: number
  started_at: string
  finished_at: string | null
  error_message: string | null
}

export interface AdminUserCreate {
  email?: string
  phone_number?: string
  first_name?: string
  last_name?: string
  full_name?: string
  dob?: string
  gender?: string
  address?: string
  password?: string
  auth_method?: 'email' | 'sms' | 'both'
  is_active?: boolean
  is_superuser?: boolean
  is_hr_admin?: boolean
  organization_id?: number
}

export interface AdminUserUpdate {
  email?: string
  phone_number?: string
  first_name?: string
  last_name?: string
  full_name?: string
  dob?: string
  gender?: string
  address?: string
  password?: string
  auth_method?: 'email' | 'sms' | 'both'
  is_active?: boolean
  is_superuser?: boolean
  is_hr_admin?: boolean
  is_verified?: boolean
  organization_id?: number
}

export interface BulkImportResult {
  row: number
  success: boolean
  user_id: number | null
  error: string | null
}

export interface BulkImportResponse {
  total: number
  success_count: number
  failure_count: number
  results: BulkImportResult[]
}

export interface DashboardStats {
  // User metrics
  total_users: number
  active_users: number
  superusers: number
  new_users_today: number
  new_users_this_week: number
  new_users_this_month: number
  users_by_auth_method: Record<string, number>

  // Engagement metrics
  total_messages: number
  messages_today: number
  messages_this_week: number
  messages_this_month: number
  messages_by_channel: Record<string, number>
  users_with_messages: number
  total_chats: number
  avg_messages_per_user: number
}

// API functions
export async function getUsers(params: UserListParams = {}): Promise<UserListResponse> {
  const response = await apiClient.get<UserListResponse>('/api/v1/admin/users', { params })
  return response.data
}

export async function getUser(id: number): Promise<User> {
  const response = await apiClient.get<User>(`/api/v1/admin/users/${id}`)
  return response.data
}

export async function createUser(data: AdminUserCreate): Promise<User> {
  const response = await apiClient.post<User>('/api/v1/admin/users', data)
  return response.data
}

export async function updateUser(id: number, data: AdminUserUpdate): Promise<User> {
  const response = await apiClient.patch<User>(`/api/v1/admin/users/${id}`, data)
  return response.data
}

export async function deleteUser(id: number): Promise<void> {
  await apiClient.delete(`/api/v1/admin/users/${id}`)
}

export async function importUsers(file: File): Promise<BulkImportResponse> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<BulkImportResponse>(
    '/api/v1/admin/users/import',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  )
  return response.data
}

export async function downloadImportTemplate(): Promise<void> {
  const response = await apiClient.get('/api/v1/admin/users/import/template', {
    responseType: 'blob',
  })
  const blob = new Blob([response.data], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'user_import_template.csv'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export async function getStats(): Promise<DashboardStats> {
  const response = await apiClient.get<DashboardStats>('/api/v1/admin/stats')
  return response.data
}

export async function refreshUserAnalytics(
  data: AdminUserAnalyticsRefreshRequest,
): Promise<AdminUserAnalyticsRefreshResponse> {
  const response = await apiClient.post<AdminUserAnalyticsRefreshResponse>(
    '/api/v1/admin/users/analytics/refresh',
    data,
  )
  return response.data
}

export async function getUserAnalyticsRefreshStatus(
  jobId: string,
): Promise<AdminUserAnalyticsRefreshStatus> {
  const response = await apiClient.get<AdminUserAnalyticsRefreshStatus>(
    '/api/v1/admin/users/analytics/refresh-status',
    { params: { job_id: jobId } },
  )
  return response.data
}

// Organization API functions
export async function getOrganizations(
  params: OrganizationListParams = {},
): Promise<OrganizationListResponse> {
  const response = await apiClient.get<OrganizationListResponse>('/api/v1/admin/organizations', {
    params,
  })
  return response.data
}

export async function getOrganization(id: number): Promise<Organization> {
  const response = await apiClient.get<Organization>(`/api/v1/admin/organizations/${id}`)
  return response.data
}

export async function createOrganization(data: OrganizationCreate): Promise<Organization> {
  const response = await apiClient.post<Organization>('/api/v1/admin/organizations', data)
  return response.data
}

export async function updateOrganization(
  id: number,
  data: OrganizationUpdate,
): Promise<Organization> {
  const response = await apiClient.patch<Organization>(`/api/v1/admin/organizations/${id}`, data)
  return response.data
}

export async function deleteOrganization(id: number): Promise<void> {
  await apiClient.delete(`/api/v1/admin/organizations/${id}`)
}

export async function getOrganizationUsers(
  id: number,
  params: { page?: number; limit?: number } = {},
): Promise<UserListResponse> {
  const response = await apiClient.get<UserListResponse>(
    `/api/v1/admin/organizations/${id}/users`,
    { params },
  )
  return response.data
}

// Book admin types
export interface AdminBook {
  id: number
  title: string
  author: string
  isbn: string | null
  category: string
  cover_url: string | null
  rating: number | null
  is_active: boolean
  generation_status: string | null
}

export interface AdminBookListResponse {
  books: AdminBook[]
  total: number
}

export interface GenerateBookResponse {
  topic_id: number
  book_id: number
  generation_status: string
  message: string
}

export interface GenerateAllBooksResponse {
  queued: number
  skipped: number
  errors: { book_id: number; title: string; error: string }[]
  message: string
}

// Book admin API functions
export async function getAdminBooks(): Promise<AdminBookListResponse> {
  const response = await apiClient.get<AdminBookListResponse>('/api/v1/admin/books')
  return response.data
}

export async function generateBookContent(bookId: number): Promise<GenerateBookResponse> {
  const response = await apiClient.post<GenerateBookResponse>(
    `/api/v1/admin/books/${bookId}/generate`,
  )
  return response.data
}

export async function generateAllBookContent(): Promise<GenerateAllBooksResponse> {
  const response = await apiClient.post<GenerateAllBooksResponse>(
    '/api/v1/admin/books/generate-all',
  )
  return response.data
}

// Podcast admin types
export interface AdminPodcast {
  id: number
  title: string
  description: string
  category: string
  cover_s3_key: string | null
  cover_url: string | null
  episode_count: number | null
  is_active: boolean
  generation_status: string | null
  audio_ready: boolean
  has_cover: boolean
}

export interface AdminPodcastListResponse {
  podcasts: AdminPodcast[]
  total: number
}

export interface AdminPodcastCreate {
  title: string
  description: string
  category: string
}

export interface GeneratePodcastResponse {
  topic_id: number
  podcast_id: number
  generation_status: string
  message: string
}

export interface GenerateAllPodcastsResponse {
  queued: number
  skipped: number
  errors: { podcast_id: number; title: string; error: string }[]
  message: string
}

// Podcast admin API functions
export async function getAdminPodcasts(): Promise<AdminPodcastListResponse> {
  const response = await apiClient.get<AdminPodcastListResponse>('/api/v1/admin/podcasts')
  return response.data
}

export async function createAdminPodcast(
  data: AdminPodcastCreate,
): Promise<{ id: number; message: string }> {
  const response = await apiClient.post('/api/v1/admin/podcasts', data)
  return response.data
}

export async function generatePodcastContent(podcastId: number): Promise<GeneratePodcastResponse> {
  const response = await apiClient.post<GeneratePodcastResponse>(
    `/api/v1/admin/podcasts/${podcastId}/generate`,
  )
  return response.data
}

export async function generateAllPodcastContent(): Promise<GenerateAllPodcastsResponse> {
  const response = await apiClient.post<GenerateAllPodcastsResponse>(
    '/api/v1/admin/podcasts/generate-all',
  )
  return response.data
}

// Safety flag API functions
export async function getSafetyFlaggedUsers(): Promise<User[]> {
  const response = await apiClient.get<User[]>('/api/v1/admin/users/safety-flagged')
  return response.data
}

export async function clearSafetyFlag(userId: number): Promise<User> {
  const response = await apiClient.post<User>(`/api/v1/admin/users/${userId}/safety/clear`)
  return response.data
}

export async function generatePodcastCover(
  podcastId: number,
): Promise<{ podcast_id: number; cover_s3_key: string; message: string }> {
  const response = await apiClient.post(`/api/v1/admin/podcasts/${podcastId}/generate-cover`)
  return response.data
}
